import { useState } from "react";

import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { withDesktopDrag } from "@/components/desktop/withDesktopDrag";
import { Text } from "@/components/flexkit/typography/Text";
import { LoginFooter } from "@/components/login/LoginFooter";
import { useListLoginProviders } from "@/queries/account-queries";
import { SSOLogin } from "@/routes/login/LoginPage";
import { LoginQuotePanel } from "@/routes/login/LoginQuotePanel";

type OnaOnboardingLayoutProps = {
    returnTo: string;
    inviteId?: string;
};

export const OnaOnboardingLayout = withDesktopDrag(function OnaOnboardingLayout({
    returnTo,
    inviteId,
}: OnaOnboardingLayoutProps) {
    const [, setShowContinueWithSSO] = useState(false);

    return (
        <div data-testid="ona-onboarding-login-page" className="flex h-full min-h-screen w-full flex-col md:flex-row">
            {/* Left panel — form */}
            <div className="flex w-full flex-1 flex-col bg-surface-primary md:w-1/2 md:border-r md:border-border-base">
                {/* Centered form content */}
                <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 md:px-16">
                    <div className="flex w-full max-w-sm flex-col items-center">
                        <OnaWordmark data-testid="ona-wordmark" className="mb-4 h-10 w-auto" />
                        <Text className="mb-8 text-center text-content-secondary">
                            Run a team of AI software engineers in the cloud
                        </Text>

                        <div className="flex w-full flex-col items-center space-y-3">
                            <LoginArea
                                inviteId={inviteId}
                                returnTo={returnTo}
                                onSSOViewChange={setShowContinueWithSSO}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer pinned to bottom of left panel */}
                <div className="px-6 pb-6 text-center">
                    <LoginFooter
                        className="text-sm text-content-tertiary"
                        linkClassName="text-sm text-content-secondary underline"
                    />
                </div>
            </div>

            {/* Right panel — testimonial quote (hidden on mobile) */}
            <div className="hidden md:block md:w-1/2">
                <LoginQuotePanel />
            </div>
        </div>
    );
});

function LoginArea({
    inviteId,
    returnTo,
    onSSOViewChange,
}: {
    inviteId?: string;
    returnTo: string;
    onSSOViewChange: (show: boolean) => void;
}) {
    const { isPending: isPendingProviders } = useListLoginProviders(inviteId);

    if (inviteId && isPendingProviders) {
        return (
            <p className="mb-6 flex items-center space-x-2 text-content-secondary">
                <IconSpinner size="lg" className="animate-spin" />
                <span>Loading login options…</span>
            </p>
        );
    }

    return (
        <SSOLogin
            inviteId={inviteId}
            returnTo={returnTo}
            className="w-full"
            hideLoginToClassic
            newLoginLayout
            onSSOViewChange={onSSOViewChange}
        />
    );
}
